import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { name: "", email: "", password: "", role: "sales" };

export const UsersPanel = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [f, setF] = useState(EMPTY);
  const load = () => api.get("/users").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/users", f); toast.success("User added"); setF(EMPTY); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (u) => {
    if (!window.confirm(`Remove ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const setRole = async (u, role) => {
    try { await api.put(`/users/${u.id}`, { role }); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={add} className="grid gap-3 rounded-lg border border-slate-200 bg-white p-5 md:grid-cols-[1fr_1fr_1fr_140px_auto]" data-testid="add-user-form">
        <Input required placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name-input" />
        <Input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email-input" />
        <Input required type="password" minLength={6} placeholder="Password (min 6)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-password-input" />
        <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
          <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="sales">Sales Rep</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
        </Select>
        <Button type="submit" className="bg-[#01195D] hover:bg-[#0A2A7A]" data-testid="user-add-btn">Add user</Button>
      </form>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th /></tr></thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className="border-t border-slate-100" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 text-slate-500">{u.email}</td>
                <td className="px-4 py-3">
                  <Select value={u.role} onValueChange={(v) => setRole(u, v)} disabled={u.id === user.id}>
                    <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="sales">Sales Rep</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3 text-right">
                  {u.id !== user.id && <Button size="icon" variant="ghost" onClick={() => del(u)} data-testid={`user-delete-${u.email}`}><Trash2 className="h-4 w-4 text-slate-400" /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
